{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2019 - 2022                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Utils;

interface

uses
  Types, Web, SysUtils, Math, Classes, JS;

const
  DayMonday = 1;
  DayTuesday = 2;
  DayWednesday = 3;
  DayThursday = 4;
  DayFriday = 5;
  DaySaturday = 6;
  DaySunday = 7;

  MonthJanuary = 1;
  MonthFebruary = 2;
  MonthMarch = 3;
  MonthApril = 4;
  MonthMay = 5;
  MonthJune = 6;
  MonthJuly = 7;
  MonthAugust = 8;
  MonthSeptember = 9;
  MonthOctober = 10;
  MonthNovember = 11;
  MonthDecember = 12;

type
  TInterfaceList = class;

  TProc = Types.TProc;
  TProcBoolean = SysUtils.TProc<Boolean>;
  TProcInteger = SysUtils.TProc<Integer>;
  TProcString = Types.TProcString;

  EConvertError = SysUtils.EConvertError;

  EEncodingError = class(Exception);

  EStreamError = Classes.EStreamError;
  EStringStreamError = class(EStreamError);
  EFilerError = Classes.EFilerError;
  EReadError = Classes.EReadError;
  EWriteError = Classes.EWriteError;

  TBytesHelper = record helper for TBytes
  strict private
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    function Compare(const ARight: TBytes): Integer;
    function High: Integer;
    function Low: Integer;
    function SubBytes(const AStartIndex: Integer): TBytes; overload; inline;
    function SubBytes(const AStartIndex, ALength: Integer): TBytes; overload; inline;
    property Length: Integer read get_Length write set_Length;
  end;

  TWords = array of Word;
  TWordsHelper = record helper for TWords
  strict private
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    class function FromBytes(const ABytes: TBytes): TWords; static;
    function High: Integer;
    function Low: Integer;
    function ToBytes: TBytes;
    property Length: Integer read get_Length write set_Length;
  end;

  TCharHelper = record helper for Char
  public const
    Length = 1;
    Size = 2;
  public
    function IsDigit: Boolean;
    function ToOrd: Integer;
    function ToUpper: Char; overload;
    class function ToUpper(const AValue: Char): Char; overload; assembler; static;
  end;

  TIntegerHelper = record helper for Integer
  public
    function Equals(const AValue: Integer): Boolean; inline;
    function NotEquals(const AValue: Integer): Boolean; inline;
    class function Parse(const AValue: string): Integer; static;
    function ToString: string; overload;
    class function ToString(const AValue: Integer): string; overload; assembler; static;
    class function TryParse(const AString: string; out AValue: Integer): Boolean; static; inline;
  end;

  TLongIntHelper = record helper for LongInt
  public
    function Equals(const AValue: LongInt): Boolean; inline;
    function NotEquals(const AValue: LongInt): Boolean; inline;
    class function Parse(const AValue: string): Integer; static;
    function ToString: string; overload;
    class function ToString(const AValue: LongInt): string; overload; assembler; static;
    class function TryParse(const AString: string; out AValue: Integer): Boolean; static; inline;
  end;

  TWordHelper = record helper for Word
  public
    class function FromBytes(const ALow, AHigh: Byte): Word; static;
    procedure ToBytes(out ALow, AHigh: Byte);
  end;

  TStringHelper = record helper for string
  strict private
    function get_Chars(const AIndex: Integer): Char; inline;
    function get_Length: Integer; inline;
    function Pos(const ASubStr, AStr: string; const AOffset: Integer): Integer;
    function Reverse: string;
    procedure set_Chars(const AIndex: Integer; const AValue: Char); inline;
    procedure set_Length(const AValue: Integer); inline;
  public const
    Empty = '';
  public
    function CompareTo(const AValue: string): Integer; overload;
    class function CompareTo(const ALeft, ARight: string): Integer; overload; assembler; static;
    function Contains(const AValue: string): Boolean;
    function Equals(const AValue: string): Boolean;
    function FirstChar: Char;
    function Format(const AArgs: array of const): string; overload;
    class function Format(const AValue: string; const AArgs: array of const): string; overload; static;
    function High: Integer; overload; inline;
    function IndexOf(const AValue: Char): Integer; overload; inline;
    function IndexOf(const AValue: string; const AStartIndex, ACount: Integer): Integer; overload;
    function IndexOf(const AValue: string): Integer; overload; inline;
    function Insert(const AStartIndex: Integer; const AValue: string): string;
    function IsEmpty: Boolean; inline;
    function IsNumber: Boolean;
    function LastIndexOf(const AValue: Char): Integer; overload; inline;
    class function Low: Integer; static; inline;
    function NotEquals(const AValue: string): Boolean;
    function NotIsEmpty: Boolean; inline;
    function PadLeft(const ATotalWidth: Integer): string; overload; inline;
    function PadLeft(ATotalWidth: Integer; const APaddingChar: Char): string; overload; inline;
    function Remove(const AStartIndex: Integer): string; overload; inline;
    function Remove(const AStartIndex, ACount: Integer): string; overload; inline;
    function Replace(const AOldValue: string; const ANewValue: string): string; overload;
    function Same(const AValue: string): Boolean; inline;
    class function StartsText(const ASubText, AText: string): Boolean; static;
    function StartsWith(const AValue: string): Boolean; overload; inline;
    function StartsWith(const AValue: string; AIgnoreCase: Boolean): Boolean; overload;
    function Substring(const AStartIndex: Integer): string; overload; inline;
    function Substring(const AStartIndex, ALength: Integer): string; overload; inline;
    function ToLower: string;
    function ToUpper: string;
    function Trim: string;
    property Chars[const AIndex: Integer]: Char read get_Chars write set_Chars;
    property Length: Integer read get_Length write set_Length;
  end;

  TDateTimeHelper = record helper for TDateTime
  public
    function Equals(const AValue: TDateTime): Boolean; inline;
    function NotEquals(const AValue: TDateTime): Boolean; inline;
    function ToString: string; inline;
  end;

  TMonthNameArray = SysUtils.TMonthNameArray;
  TMonthNames = SysUtils.TMonthNames;
  TDayNames = SysUtils.TDayNames;

  TFormatSettings = SysUtils.TFormatSettings;

  TTMSEncoding = class(TObject)
  strict private class var
    FANSI: TTMSEncoding;
    FUnicode: TTMSEncoding;
  strict private
    class function get_ANSI: TTMSEncoding; static;
    class function get_Default: TTMSEncoding; static;
    class function get_Unicode: TTMSEncoding; static;
  public
    function GetByteCount(const AChars: string): Integer; overload; virtual; abstract;
    function GetByteCount(const AChars: string; const ACharCount: Integer): Integer; overload;
    function GetByteCount(const AChars: string; const ACharIndex, ACharCount: Integer): Integer; overload; inline;
    function GetBytes(const AChars: string): TBytes; overload; virtual; abstract;
    function GetBytes(const AChars: string; const ACharCount: Integer): TBytes; overload;
    function GetBytes(const AChars: string; const ACharIndex, ACharCount: Integer): TBytes; overload; inline;
    function GetString(const ABytes: TBytes): string; overload; virtual; abstract;
    function GetString(const ABytes: TBytes; const ACharCount: Integer): string; overload;
    function GetString(const ABytes: TBytes; const ACharIndex, ACharCount: Integer): string; overload; inline;
    class function IsStandardEncoding(AEncoding: TTMSEncoding): Boolean; static;
    class property ANSI: TTMSEncoding read get_ANSI;
    class property Default: TTMSEncoding read get_Default;
    class property Unicode: TTMSEncoding read get_Unicode;
  end;

  TANSIEncoding = class(TTMSEncoding)
  public
    function GetByteCount(const AChars: string): Integer; overload; override;
    function GetBytes(const AChars: string): TBytes; overload; override;
    function GetString(const ABytes: TBytes): string; overload; override;
  end;

  TUnicodeEncoding = class(TTMSEncoding)
  strict private
    procedure InternalGetBytes(const AChars: string; out ABytes: TBytes);
  public
    function GetByteCount(const AChars: string): Integer; overload; override;
    function GetBytes(const AChars: string): TBytes; overload; override;
    function GetString(const ABytes: TBytes): string; overload; override;
  end;

  TStreamHelper = class helper for TStream
  public
    function CopyFrom(const ASource: TStream; ACount: Int64): Int64;
  end;

  TTMSStringStream = class(TBytesStream)
  private
    FEncoding: TTMSEncoding;
    FFileName: string;
    FOwnsEncoding: Boolean;
    FError: TProcString;
    FLoaded: TProc;
    FRequest: TJSXMLHTTPRequest;
    function GetDataString: string;
    function InternalLoadFromFileP(const AFileName: string): TJSPromise;
    function OnAbort(AEvent: TEventListenerEvent): Boolean;
    function OnError(AEvent: TEventListenerEvent): Boolean;
    function OnLoad(AEvent: TEventListenerEvent): Boolean;
  public
    constructor Create; overload;
    constructor Create(const AString: string); overload;
    constructor Create(const AString: string; AEncoding: TTMSEncoding; AOwnsEncoding: Boolean = True); overload;
    destructor Destroy; override;
    procedure LoadFromFile(const AFileName: string); overload;
    procedure LoadFromFile(const AFileName: string; const ALoaded: TProc); overload;
    procedure LoadFromFile(const AFileName: string; const ALoaded: TProc; const AError: TProcString); overload; reintroduce;
    function LoadFromFileP(const AFileName: string): Boolean; async;
    function ReadString(ACount: Integer): string;
    procedure WriteString(const AString: string);
    property DataString: string read GetDataString;
  end;

  IInterfaceList = interface
  ['{285DEA8A-B865-11D1-AAA7-00C04FB17A72}']
    function Add(const AItem: IInterface): Integer;
    procedure Clear;
    procedure Delete(const AIndex: Integer);
    procedure Exchange(const AIndex1, AIndex2: Integer);
    function Expand: TInterfaceList;
    function First: IInterface;
    function get_Capacity: Integer;
    function get_Count: Integer;
    function get_Items(const AIndex: Integer): IInterface;
    function IndexOf(const AItem: IInterface): Integer;
    function IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure Insert(const AIndex: Integer; const AItem: IInterface);
    function Last: IInterface;
    procedure Lock;
    function Remove(const AItem: IInterface): Integer;
    function RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure set_Capacity(const AValue: Integer);
    procedure set_Count(const AValue: Integer);
    procedure set_Items(const AIndex: Integer; const AItem: IInterface);
    procedure Unlock;
    property Count: Integer read get_Count write set_Count;
    property Items[const AIndex: Integer]: IInterface read get_Items write set_Items; default;
  end;

  TInterfaceListEnumerator = class(TObject)
  strict private
    FIndex: Integer;
    FInterfaceList: TInterfaceList;
    function get_Current: IInterface; inline;
  public
    constructor Create(const AInterfaceList: TInterfaceList);
    function MoveNext: Boolean;
    property Current: IInterface read get_Current;
  end;

  IInterfaceListEx = interface(IInterfaceList)
    ['{FDB39D70-65B9-4995-9436-6084ACA05DB3}']
    function GetEnumerator: TInterfaceListEnumerator;
  end;

  TCompareItems = reference to function(const ALeft, ARight: IInterface): TValueRelationship;

  IInterfaceListEx2 = interface(IInterfaceList)
    ['{4FE71218-0F8F-4027-B050-5E84E2202604}']
    procedure Sort(const ACompare: TCompareItems);
  end;

  TInterfaceList = class(TInterfacedObject, IInterfaceList, IInterfaceListEx, IInterfaceListEx2)
  strict private
    FList: TFPList;
  strict protected
    function Add(const AItem: IInterface): Integer;
    procedure Clear;
    procedure Delete(const AIndex: Integer);
    procedure Exchange(const AIndex1, AIndex2: Integer);
    function Expand: TInterfaceList;
    function First: IInterface;
    function GetEnumerator: TInterfaceListEnumerator;
    function get_Capacity: Integer;
    function get_Count: Integer;
    function get_Items(const AIndex: Integer): IInterface;
    function IndexOf(const AItem: IInterface): Integer;
    function IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure Insert(const AIndex: Integer; const AItem: IInterface);
    function Last: IInterface;
    procedure Lock;
    function Remove(const AItem: IInterface): Integer;
    function RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
    procedure set_Capacity(const AValue: Integer);
    procedure set_Count(const AValue: Integer);
    procedure set_Items(const AIndex: Integer; const AItem: IInterface);
    procedure Sort(const ACompare: TCompareItems);
    procedure Unlock;
    property Capacity: Integer read get_Capacity write set_Capacity;
    property Items[const AIndex: Integer]: IInterface read get_Items write set_Items; default;
  protected
    property Count: Integer read get_Count write set_Count;
  public
    constructor Create;
    destructor Destroy; override;
  end;

  TStringBuilder = class(TObject)
  strict private
    FData: string;
    function get_Length: Integer;
    procedure set_Length(const AValue: Integer);
  public
    constructor Create(const AValue: string); overload;
    function Append(const AValue: Integer): TStringBuilder; overload;
    function Append(const AValue: string): TStringBuilder; overload;
    function Insert(const AIndex: Integer; const AValue: Integer): TStringBuilder; overload;
    function Insert(const AIndex: Integer; const AValue: string): TStringBuilder; overload;
    function ToString: string; overload; override;
    property Length: Integer read get_Length write set_Length;
  end;

procedure UseVariable(const AValue: IInterface); overload;
procedure UseVariable(const AValue: Integer); overload;
procedure UseVariable(const AValue: string); overload;

function ArrayBufferToBase64(Buffer: TJSArrayBuffer): string;
function Base64ToArrayBuffer(Base64: string): TJSArrayBuffer;
function StringToBase64(AValue: string): string;
function Base64ToString(AValue: string): string;

function SizeOf(const AValue: Double): Integer; overload;
function SizeOf(const AValue: Integer): Integer; overload;

procedure InsertAfter(newNode, referenceNode: TJSElement);

function MatchStrEx(AValue1, AValue2: string; ACaseSensitive: Boolean = true): Boolean;
function MatchStr(AValue1, AValue2:string; ACaseSensitive:Boolean = true):Boolean;

procedure SelectElementContents(AElement: TJSElement);

function FormatProp(const AFmt: string; const AArgs: array of const): string;

function StringIsFloat(N: string): boolean;
function StringIsInteger(N: string): boolean;

function MD5Hash(N: string): string;

implementation

uses
  RTLConsts, WEBLib.Consts;

var
  FCompare: TCompareItems;

procedure UseVariable(const AValue: IInterface);
begin
end;

procedure UseVariable(const AValue: Integer);
begin
end;

procedure UseVariable(const AValue: string);
begin
end;

{ TBytesHelper }

function TBytesHelper.Compare(const ARight: TBytes): Integer;
var
  lCount: Integer;
begin
  if Length > ARight.Length then
    Exit(GreaterThanValue);

  if Length < ARight.Length then
    Exit(LessThanValue);

  Result := EqualsValue;
  for lCount := Low to High do
  begin
    if Self[lCount] > ARight[lCount] then
      Exit(GreaterThanValue);
    if Self[lCount] < ARight[lCount] then
      Exit(LessThanValue);
  end;
end;

function TBytesHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TBytesHelper.High: Integer;
begin
  Result := System.High(Self);
end;

function TBytesHelper.Low: Integer;
begin
  Result := System.Low(Self);
end;

procedure TBytesHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TBytesHelper.SubBytes(const AStartIndex: Integer): TBytes;
begin
  Result := System.Copy(Self, AStartIndex, Length);
end;

function TBytesHelper.SubBytes(const AStartIndex, ALength: Integer): TBytes;
begin
  Result := System.Copy(Self, AStartIndex, ALength);
end;

{ TWordsHelper }

class function TWordsHelper.FromBytes(const ABytes: TBytes): TWords;
const
  cBytesPerWord = 2;
var
  lCount: Integer;
  lLow: Byte;
  lHigh: Byte;
begin
  lCount := ABytes.Length;
  if Odd(lCount) then
    Inc(lCount);
  Result.Length := lCount div cBytesPerWord;

  lCount := ABytes.Low;
  while lCount <= ABytes.High do
  begin
    lLow := ABytes[lCount];
    if lCount + 1 <= ABytes.High then
      lHigh := ABytes[lCount + 1]
    else
      lHigh := 0;

    Result[lCount div cBytesPerWord] := Word.FromBytes(lLow, lHigh);
    Inc(lCount, cBytesPerWord);
  end;

end;

function TWordsHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TWordsHelper.High: Integer;
begin
  Result := System.High(Self);
end;

function TWordsHelper.Low: Integer;
begin
  Result := System.Low(Self);
end;

procedure TWordsHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

function TWordsHelper.ToBytes: TBytes;
const
  cBytesPerWord = 2;
var
  lLow: Byte;
  lHigh: Byte;
  lCount: Integer;
  lNewIndex: Integer;
begin
  Result.Length := Length * cBytesPerWord;
  for lCount := Low to High do
  begin
    Self[lCount].ToBytes(lLow, lHigh);
    lNewIndex := lCount * cBytesPerWord;
    Result[lNewIndex] := lLow;
    Result[lNewIndex + 1] := lHigh;
  end;
end;

{ TCharHelper }

function TCharHelper.IsDigit: Boolean;
const
  cNumChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
begin
  Result := Self in cNumChars;
end;

function TCharHelper.ToOrd: Integer;
begin
  Result := Ord(Self);
end;

function TCharHelper.ToUpper: Char;
begin
  Result := ToUpper(Self);
end;

class function TCharHelper.ToUpper(const AValue: Char): Char; assembler;
asm
  return AValue.toUpperCase();
end;

{ TIntegerHelper }

function TIntegerHelper.Equals(const AValue: Integer): Boolean;
begin
  Result := Self = AValue;
end;

function TIntegerHelper.NotEquals(const AValue: Integer): Boolean;
begin
  Result := not Equals(AValue);
end;

class function TIntegerHelper.Parse(const AValue: string): Integer;
begin
  Result := StrToInt(AValue);
end;

function TIntegerHelper.ToString: string;
begin
  Result := ToString(Self);
end;

class function TIntegerHelper.ToString(const AValue: Integer): string; assembler;
asm
  return AValue.toString();
end;

class function TIntegerHelper.TryParse(const AString: string; out AValue: Integer): Boolean;
begin
  Result := TryStrToInt(AString, AValue);
end;

{ TLongIntHelper }

function TLongIntHelper.Equals(const AValue: LongInt): Boolean;
begin
  Result := Self = AValue;
end;

function TLongIntHelper.NotEquals(const AValue: LongInt): Boolean;
begin
  Result := not Equals(AValue);
end;

class function TLongIntHelper.Parse(const AValue: string): Integer;
begin
  Result := StrToInt(AValue);
end;

function TLongIntHelper.ToString: string;
begin
  Result := ToString(Self);
end;

class function TLongIntHelper.ToString(const AValue: LongInt): string;
asm
  return AValue.toString();
end;

class function TLongIntHelper.TryParse(const AString: string; out AValue: Integer): Boolean;
begin
  Result := TryStrToInt(AString, AValue);
end;

{ TWordHelper }

class function TWordHelper.FromBytes(const ALow, AHigh: Byte): Word;
const
  cBitPerByte = 8;
begin
  Result := ALow + (AHigh shl cBitPerByte);
end;

procedure TWordHelper.ToBytes(out ALow, AHigh: Byte);
const
  cMask: Byte = $FF;
  cBitPerByte = 8;
begin
  ALow := Self and cMask;
  AHigh := (Self and (cMask shl cBitPerByte)) shr cBitPerByte;
end;

{ TStringHelper }

function TStringHelper.CompareTo(const AValue: string): Integer;
begin
  Result := CompareTo(Self, AValue);
end;

class function TStringHelper.CompareTo(const ALeft, ARight: string): Integer; assembler;
asm
  return ALeft.localeCompare(ARight);
end;

function TStringHelper.Contains(const AValue: string): Boolean;
begin
  Result := IndexOf(AValue) >= 0;
end;

function TStringHelper.Equals(const AValue: string): Boolean;
begin
  Result := Self = AValue;
end;

function TStringHelper.FirstChar: Char;
begin
  if NotIsEmpty then
    Result := Chars[0]
  else
    Result := #0;
end;

function TStringHelper.Format(const AArgs: array of const): string;
begin
  Result := SysUtils.Format(Self, AArgs);
end;

class function TStringHelper.Format(const AValue: string; const AArgs: array of const): string;
begin
  Result := SysUtils.Format(AValue, AArgs);
end;

function TStringHelper.get_Chars(const AIndex: Integer): Char;
begin
  Result := Self[AIndex + 1];
end;

function TStringHelper.get_Length: Integer;
begin
  Result := System.Length(Self);
end;

function TStringHelper.High: Integer;
begin
  Result := Length - 1;
end;

function TStringHelper.IndexOf(const AValue: Char): Integer;
begin
  Result := Pos(AValue, Self, 1) - 1;
end;

function TStringHelper.IndexOf(const AValue: string; const AStartIndex, ACount: Integer): Integer;
begin
  Result := Pos(AValue, Self, AStartIndex + 1) - 1;
  if (Result + AValue.Length) > (AStartIndex + ACount) then
    Result := -1;
end;

function TStringHelper.IndexOf(const AValue: string): Integer;
begin
  Result := System.Pos(AValue, Self) - 1;
end;

function TStringHelper.Insert(const AStartIndex: Integer; const AValue: string): string;
begin
  System.Insert(AValue, Self, AStartIndex + 1);
  Result := Self;
end;

function TStringHelper.IsEmpty: Boolean;
begin
  Result := Self = Empty;
end;

function TStringHelper.IsNumber: Boolean;
const
  cNumbers = ['0'..'9'];
var
  lCount: Integer;
begin
  if IsEmpty then
    Exit(False);

  for lCount := 0 to Length - 1 do
  begin
    if not (Chars[lCount] in cNumbers) then
      Exit(False);
  end;
  Result := True;
end;

function TStringHelper.LastIndexOf(const AValue: Char): Integer;
begin
  Result := Reverse.IndexOf(AValue);
  if Result >= 0 then
    Result := Length - Result - 1;
end;

class function TStringHelper.Low: Integer;
begin
  Result := 0;
end;

function TStringHelper.NotEquals(const AValue: string): Boolean;
begin
  Result := not Equals(AValue);
end;

function TStringHelper.NotIsEmpty: Boolean;
begin
  Result := Self <> Empty;
end;

function TStringHelper.PadLeft(const ATotalWidth: Integer): string;
const
  cDefPadChar = ' ';
begin
  Result := PadLeft(ATotalWidth, cDefPadChar);
end;

function TStringHelper.PadLeft(ATotalWidth: Integer; const APaddingChar: Char): string;
begin
  ATotalWidth := ATotalWidth - Length;
  if ATotalWidth > 0 then
    Result := System.StringOfChar(APaddingChar, ATotalWidth) + Self
  else
    Result := Self;
end;

function TStringHelper.Pos(const ASubStr, AStr: string; const AOffset: Integer): Integer;
asm
  return AStr.indexOf(ASubStr, AOffset - 1) + 1;
end;

function TStringHelper.Remove(const AStartIndex: Integer): string;
begin
  Result := Remove(AStartIndex, 1);
end;

function TStringHelper.Remove(const AStartIndex, ACount: Integer): string;
begin
  Result := SubString(0, AStartIndex - 1) + SubString(AStartIndex + ACount);
end;

function TStringHelper.Replace(const AOldValue, ANewValue: string): string;
begin
  Result := SysUtils.StringReplace(Self, AOldValue, ANewValue, [rfReplaceAll]);
end;

function TStringHelper.Reverse: string;
var
  lCount: Integer;
begin
  Result := Empty;
  Result.Length := Length;
  for lCount := 0 to Length - 1 do
    Result.Chars[Length - lCount - 1] := Self.Chars[lCount];
end;

function TStringHelper.Same(const AValue: string): Boolean;
begin
  Result := SysUtils.SameText(Self, AValue);
end;

procedure TStringHelper.set_Chars(const AIndex: Integer; const AValue: Char);
begin
  Self[AIndex + 1] := AValue;
end;

procedure TStringHelper.set_Length(const AValue: Integer);
begin
  System.SetLength(Self, AValue);
end;

class function TStringHelper.StartsText(const ASubText, AText: string): Boolean;
begin
  if ASubText.IsEmpty then
    Result := True
  else
  begin
    if AText.Length >= ASubText.Length then
      Result := SysUtils.SameText(AText.Substring(0, ASubText.Length), ASubText)
    else
      Result := False;
  end;
end;

function TStringHelper.StartsWith(const AValue: string): Boolean;
begin
  Result := StartsWith(AValue, False);
end;

function TStringHelper.StartsWith(const AValue: string; AIgnoreCase: Boolean): Boolean;
begin
  if AIgnoreCase then
    Result := StartsText(AValue, Self)
  else
  if AValue = Empty then
    Result := True
  else
  begin
    if (Self.Length >= AValue.Length) then
      Result := Substring(0, AValue.Length).Equals(AValue)
    else
      Result := False;
  end;
end;

function TStringHelper.Substring(const AStartIndex: Integer): string;
begin
  Result := System.Copy(Self, AStartIndex + 1, Self.Length);
end;

function TStringHelper.Substring(const AStartIndex, ALength: Integer): string;
begin
  Result := System.Copy(Self, AStartIndex + 1, ALength);
end;

function TStringHelper.ToLower: string;
var
  lValue: string;
begin
  lValue := Self;
  asm
    lValue = lValue.toLowerCase();
  end;
  Result := lValue;
end;

function TStringHelper.ToUpper: string;
var
  lValue: string;
begin
  lValue := Self;
  asm
    lValue = lValue.toUpperCase();
  end;
  Result := lValue;
end;

function TStringHelper.Trim: string;
begin
  Result := SysUtils.Trim(Self);
end;

{ TDateTimeHelper }

function TDateTimeHelper.Equals(const AValue: TDateTime): Boolean;
begin
  Result := SameValue(Self, AValue);
end;

function TDateTimeHelper.NotEquals(const AValue: TDateTime): Boolean;
begin
  Result := not SameValue(Self, AValue);
end;

function TDateTimeHelper.ToString: string;
begin
  Result := SysUtils.DateTimeToStr(Self, FormatSettings);
end;

{ TTMSEncoding }

function TTMSEncoding.GetByteCount(const AChars: string; const ACharCount: Integer): Integer;
begin
  Result := GetByteCount(AChars.Substring(0, ACharCount));
end;

function TTMSEncoding.GetByteCount(const AChars: string; const ACharIndex, ACharCount: Integer): Integer;
begin
  Result := GetByteCount(AChars.Substring(ACharIndex, ACharCount));
end;

function TTMSEncoding.GetBytes(const AChars: string; const ACharCount: Integer): TBytes;
begin
  Result := GetBytes(AChars.Substring(0, ACharCount));
end;

function TTMSEncoding.GetBytes(const AChars: string; const ACharIndex, ACharCount: Integer): TBytes;
begin
  Result := GetBytes(AChars.Substring(ACharIndex, ACharCount));
end;

function TTMSEncoding.GetString(const ABytes: TBytes; const ACharCount: Integer): string;
begin
  Result := GetString(ABytes.SubBytes(0, ACharCount));
end;

function TTMSEncoding.GetString(const ABytes: TBytes; const ACharIndex, ACharCount: Integer): string;
begin
  Result := GetString(ABytes.SubBytes(ACharIndex, ACharCount));
end;

class function TTMSEncoding.get_ANSI: TTMSEncoding;
begin
  if FANSI = nil then
    FANSI := TANSIEncoding.Create;
  Result := FANSI;
end;

class function TTMSEncoding.get_Default: TTMSEncoding;
begin
  Result := ANSI;
end;

class function TTMSEncoding.get_Unicode: TTMSEncoding;
begin
  if FUnicode = nil then
    FUnicode := TUnicodeEncoding.Create;
  Result := FUnicode;
end;

class function TTMSEncoding.IsStandardEncoding(AEncoding: TTMSEncoding): Boolean;
begin
  Result := (AEncoding <> nil) and ((AEncoding = FANSI) or (AEncoding = FUnicode));
end;

{ TANSIEncoding }

function TANSIEncoding.GetByteCount(const AChars: string): Integer;
begin
  Result := AChars.Length;
end;

function TANSIEncoding.GetBytes(const AChars: string): TBytes;
begin
  Result := BytesOf(AChars);
end;

function TANSIEncoding.GetString(const ABytes: TBytes): string;
begin
  Result := StringOf(ABytes);
end;

{ TUnicodeEncoding }

function TUnicodeEncoding.GetByteCount(const AChars: string): Integer;
begin
  Result := Char.Size * AChars.Length;
end;

function TUnicodeEncoding.GetBytes(const AChars: string): TBytes;
begin
  InternalGetBytes(AChars, Result);
end;

function TUnicodeEncoding.GetString(const ABytes: TBytes): string;
var
  lWords: TWords;
  lBuffer: string;
begin
  lBuffer := string.Empty;
  lWords := TWords.FromBytes(ABytes);
  if lWords <> nil then
  begin
    asm
      for (var lCount = 0; lCount < lWords.length; lCount++)
        lBuffer += string.fromCharCode(lWords[lCount]);
    end;
  end;
  Result := lBuffer;
end;

procedure TUnicodeEncoding.InternalGetBytes(const AChars: string; out ABytes: TBytes);
var
  lWords: TWords;
begin
  lWords := nil;
  asm
    for (var lCount = 0; lCount < AChars.length; lCount++)
      lWords.push(AChars.charCodeAt(lCount));
  end;
  ABytes := lWords.ToBytes;
end;

{ TStreamHelper }

function TStreamHelper.CopyFrom(const ASource: TStream; ACount: Int64): Int64;
const
  MaxBufSize = $F000;
var
  lBufSize: Integer;
  lCount: Integer;
  lBuffer: TBytes;
begin
  if ACount <= 0 then
  begin
    ASource.Position := 0;
    ACount := ASource.Size;
  end;
  Result := ACount;
  if ACount > MaxBufSize then
    lBufSize := MaxBufSize
  else
    lBufSize := ACount;
  lBuffer.Length := lBufSize;
  try
    while ACount <> 0 do
    begin
      if ACount > lBufSize then
        lCount := lBufSize
      else
        lCount := ACount;
      ASource.ReadBuffer(lBuffer, lCount);
      WriteBuffer(lBuffer, lCount);
      Dec(ACount, lCount);
    end;
  finally
    lBuffer.Length := 0;
  end;
end;

{ TTMSStringStream }

constructor TTMSStringStream.Create;
begin
  Create(string.Empty, TTMSEncoding.Default, False);
end;

constructor TTMSStringStream.Create(const AString: string);
begin
  Create(AString, TTMSEncoding.Default, False);
end;

constructor TTMSStringStream.Create(const AString: string; AEncoding: TTMSEncoding; AOwnsEncoding: Boolean);
var
  lBytes: TBytes;
begin
  inherited Create;
  FEncoding := AEncoding;
  FOwnsEncoding := AOwnsEncoding and not TTMSEncoding.IsStandardEncoding(AEncoding);
  lBytes := FEncoding.GetBytes(AString);
  SetPointer(BytesToMemory(lBytes), lBytes.Length);
end;

destructor TTMSStringStream.Destroy;
begin
  if FOwnsEncoding then
    FEncoding.Free;
  inherited Destroy;
end;

function TTMSStringStream.GetDataString: string;
begin
  Result := FEncoding.GetString(Copy(Bytes, 0, Size));
end;

procedure TTMSStringStream.LoadFromFile(const AFileName: string);
const
  cStatusOK = 200;
var
  lStatus: Integer;
  lResponse: string;
begin
  lResponse := string.Empty;
  lStatus := cStatusOK;
  asm
    var lRequest = new XMLHttpRequest();
    lRequest.open("GET", AFileName, false);
    lRequest.send();

    if (lRequest.status == cStatusOK)
      lResponse = lRequest.responseText
    else
      lStatus = lRequest.status;
  end;

  if lStatus <> cStatusOK then
    raise EStringStreamError.CreateFmt(SCannotGetFile, [AFileName, lStatus]);
  WriteString(lResponse);
end;

procedure TTMSStringStream.LoadFromFile(const AFileName: string; const ALoaded: TProc);
begin
  LoadFromFile(AFileName, ALoaded, nil);
end;

procedure TTMSStringStream.LoadFromFile(const AFileName: string; const ALoaded: TProc; const AError: TProcString);
const
  cAbortMethod = 'abort';
  cErrorMethod = 'error';
  cGetMethod = 'GET';
  cLoadMethod = 'load';
begin
  FLoaded := ALoaded;
  FError := AError;
  FFileName := AFileName;
  FRequest := TJSXMLHttpRequest.New;
  FRequest.AddEventListener(cAbortMethod, @OnAbort);
  FRequest.AddEventListener(cErrorMethod, @OnError);
  FRequest.AddEventListener(cLoadMethod, @OnLoad);
  try
    FRequest.Open(cGetMethod, AFileName, True);
    FRequest.Send;
  except
    on E: Exception do
    begin
      if Assigned(AError) then
        AError(E.Message)
      else
        raise;
    end;
  end;
end;

function TTMSStringStream.InternalLoadFromFileP(const AFileName: string): TJSPromise;
begin
  Result := TJSPromise.New(
    procedure(AResolve, AReject : TJSPromiseResolver)
    begin
      LoadFromFile(AFileName,
      procedure()
      begin
        AResolve(True);
      end,
      procedure(const AError: string)
      begin
        AReject(AError);
      end);
    end);
end;

function TTMSStringStream.LoadFromFileP(const AFileName: string): Boolean; async;
begin
  Result := await(Boolean, InternalLoadFromFileP(AFileName));
end;

function TTMSStringStream.OnAbort(AEvent: TEventListenerEvent): Boolean;
begin
  Result := True;
  if Assigned(FError) then
    FError(Format(SLoadFromFileAborted, [FFileName]));
end;

function TTMSStringStream.OnError(AEvent: TEventListenerEvent): Boolean;
begin
  Result := True;
  if Assigned(FError) then
    FError(Format(SErrorLoadFromFile, [FFileName]));
end;

function TTMSStringStream.OnLoad(AEvent: TEventListenerEvent): Boolean;
const
  cStatusOK = 200;
  cStatusLastOK = 208;
var
  lReq: TJSXMLHttpRequest;
begin
  lReq := TJSXMLHttpRequest(AEvent.target);

  if (lReq.Status < cStatusOK) or (lReq.Status > cStatusLastOK) then
  begin
    if Assigned(FError) then
    begin
      FError(Format(SCannotGetFile, [FFileName, FRequest.Status]));
      Exit;
    end
    else
      raise EStringStreamError.CreateFmt(SCannotGetFile, [FFileName, lReq.Status]);
  end;
  WriteString(lReq.ResponseText);
  Result := True;
  if Assigned(FLoaded) then
    FLoaded();
end;

function TTMSStringStream.ReadString(ACount: Integer): string;
var
  lBytes: TBytes;
  lDest: TBytes;
  lCount: Integer;
begin
  if ACount > Size - Position then
    ACount := Size - Position;

  lDest.Length := ACount;
  lBytes := Bytes;
  for lCount := Position to ACount + Position - 1 do
    lDest[lCount - Position] := lBytes[lCount];

  Result := FEncoding.GetString(lDest);
  Position := Position + ACount;
end;

procedure TTMSStringStream.WriteString(const AString: string);
var
  lBytes: TBytes;
  lData: string;
begin
  lBytes := FEncoding.GetBytes(AString);
  Write(lBytes, 0, lBytes.Length);

  lData := DataString;
  if lData <> AString then
    WriteString(''); 
end;

{ TInterfaceList }

constructor TInterfaceList.Create;
begin
  inherited Create;
  FList := TFPList.Create;
end;

destructor TInterfaceList.Destroy;
begin
  Clear;
  FList.Free;
  inherited Destroy;
end;

function TInterfaceList.Add(const AItem: IInterface): Integer;
begin
  Result := FList.Add(nil);
  Items[Result] := AItem;
end;

procedure TInterfaceList.Clear;
var
  lCount: Integer;
begin
  for lCount := 0 to FList.Count - 1 do
    Items[lCount] := nil;
  FList.Clear;
end;

procedure TInterfaceList.Delete(const AIndex: Integer);
begin
  Items[AIndex] := nil;
  FList.Delete(AIndex);
end;

procedure TInterfaceList.Exchange(const AIndex1, AIndex2: Integer);
begin
  FList.Exchange(AIndex1, AIndex2);
end;

function TInterfaceList.Expand: TInterfaceList;
begin
  FList.Expand;
  Result := Self;
end;

function TInterfaceList.First: IInterface;
begin
  Result := Items[0];
end;

function TInterfaceList.GetEnumerator: TInterfaceListEnumerator;
begin
  Result := TInterfaceListEnumerator.Create(Self);
end;

function TInterfaceList.get_Capacity: Integer;
begin
  Result := FList.Capacity;
end;

function TInterfaceList.get_Count: Integer;
begin
  Result := FList.Count;
end;

function TInterfaceList.get_Items(const AIndex: Integer): IInterface;
begin
  Result := IInterface(FList[AIndex]);
end;

function TInterfaceList.IndexOf(const AItem: IInterface): Integer;
begin
  Result := IndexOfItem(AItem, TDirection.FromBeginning);
end;

function TInterfaceList.IndexOfItem(const AItem: IInterface; const ADirection: TDirection): Integer;
begin
  Result := FList.IndexOfItem(AItem, ADirection);
end;

procedure TInterfaceList.Insert(const AIndex: Integer; const AItem: IInterface);
begin
  FList.Insert(AIndex, nil);
  Items[AIndex] := AItem;
end;

function TInterfaceList.Last: IInterface;
begin
  Result := Items[Count - 1];
end;

procedure TInterfaceList.Lock;
begin
end;

function TInterfaceList.Remove(const AItem: IInterface): Integer;
begin
  Result := RemoveItem(AItem, TDirection.FromBeginning);
end;

function TInterfaceList.RemoveItem(const AItem: IInterface; const ADirection: TDirection): Integer;
begin
  Result := FList.IndexOfItem(AItem, ADirection);
  if Result > -1 then
    Delete(Result);
end;

procedure TInterfaceList.set_Capacity(const AValue: Integer);
begin
  FList.Capacity := AValue;
end;

procedure TInterfaceList.set_Count(const AValue: Integer);
begin
  FList.Count := AValue;
end;

procedure TInterfaceList.set_Items(const AIndex: Integer; const AItem: IInterface);
begin
  if FList[AIndex] <> nil then
    IInterface(FList[AIndex])._Release;
  FList[AIndex] := AItem;
  if AItem <> nil then
    AItem._AddRef;
end;

function InternalCompare(AItem1, AItem2: JSValue): Integer;
begin
  Result := FCompare(IInterface(AItem1), IInterface(AItem2));
end;

procedure TInterfaceList.Sort(const ACompare: TCompareItems);
begin
  FCompare := ACompare;
  try
    FList.Sort(@InternalCompare);
  finally
    FCompare := nil;
  end;
end;

procedure TInterfaceList.Unlock;
begin
end;

{ TInterfaceListEnumerator }

constructor TInterfaceListEnumerator.Create(const AInterfaceList: TInterfaceList);
begin
  inherited Create;
  FIndex := -1;
  FInterfaceList := AInterfaceList;
end;

function TInterfaceListEnumerator.get_Current: IInterface;
begin
  Result := FInterfaceList[FIndex];
end;

function TInterfaceListEnumerator.MoveNext: Boolean;
begin
  Result := FIndex < FInterfaceList.Count - 1;
  if Result then
    Inc(FIndex);
end;

{ TStringBuilder }

constructor TStringBuilder.Create(const AValue: string);
begin
  inherited Create;
  FData := AValue;
end;

function TStringBuilder.Append(const AValue: Integer): TStringBuilder;
begin
  Result := Append(AValue.ToString);
end;

function TStringBuilder.Append(const AValue: string): TStringBuilder;
begin
  FData := FData + AValue;
  Result := Self;
end;

function TStringBuilder.get_Length: Integer;
begin
  Result := FData.Length;
end;

function TStringBuilder.Insert(const AIndex, AValue: Integer): TStringBuilder;
begin
  Result := Insert(AIndex, AValue.ToString);
end;

function TStringBuilder.Insert(const AIndex: Integer; const AValue: string): TStringBuilder;
begin
  FData.Insert(AIndex, AValue);
  Result := Self;
end;

procedure TStringBuilder.set_Length(const AValue: Integer);
begin
  FData.Length := AValue;
end;

function TStringBuilder.ToString: string;
begin
  Result := FData;
end;

{$HINTS OFF}

function StringToBase64(AValue: string): string;
var
  res: string;
begin
  asm
    res = window.btoa(AValue);
  end;
  Result := res;
end;

function Base64ToString(AValue: string): string;
var
  res: string;
begin
  asm
    res = window.atob(AValue);
  end;
  Result := res;
end;

function ArrayBufferToBase64(Buffer: TJSArrayBuffer): string;
begin
  asm
    function _arrayBufferToBase64(buf) {
    var binary = '';
    var bytes = new Uint8Array(buf);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
        binary += String.fromCharCode( bytes[i] );
    }
    return window.btoa(binary);
    }
    Result = _arrayBufferToBase64(Buffer);
  end;
end;

function Base64ToArrayBuffer(Base64: string): TJSArrayBuffer;
begin
  asm
    function _base64ToArrayBuffer(data) {
        var binary_string = window.atob(data);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    }

    Result = _base64ToArrayBuffer(Base64);
  end;
end;

function SizeOf(const AValue: Double): Integer; overload;
const
  cSizeOfDouble = 8;
begin
  Result := cSizeOfDouble;
end;

function SizeOf(const AValue: Integer): Integer; overload;
const
  cSizeOfInteger = 4;
begin
  Result := cSizeOfInteger;
end;

procedure InsertAfter(newNode, referenceNode: TJSElement);
begin
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
end;

function StripThousandSep(ps: string): string;
begin
  while (Pos(FormatSettings.ThousandSeparator, ps) > 0) do
    Delete(ps, Pos(FormatSettings.ThousandSeparator, ps), 1);
  Result := ps;
end;

function VarPos(ASubValue, AValue: string; var AResult: Integer): Integer;
begin
  AResult := Pos(ASubValue, AValue);
  Result := AResult;
end;

function IsDate(AValue: string; var ADate: TDateTime): boolean;
var
  su, ts: string;
  da,mo,ye,ho,mi,se: Word;
  err: Integer;
  dp,mp,yp,vp: Integer;
begin
  Result := False;
  ts := '';
  su := UpperCase(FormatSettings.ShortDateFormat);
  dp := pos('D',su);
  mp := pos('M',su);
  yp := pos('Y',su);

  da := 0;
  mo := 0;
  ye := 0;
  ho := 0;
  mi := 0;
  se := 0;

  vp := -1;
  if VarPos(FormatSettings.DateSeparator,AValue,vp)>0 then
  begin
    su := Copy(AValue,1,vp - 1);

    if (dp<mp) and
       (dp<yp) then
       val(su,da,err)
    else
    if (mp<dp) and
       (mp<yp) then
       val(su,mo,err)
    else
    if (yp<mp) and
       (yp<dp) then
       val(su,ye,err);

    if err<>0 then Exit;
    Delete(AValue,1,vp);

    if VarPos(FormatSettings.DateSeparator,AValue,vp)>0 then
    begin
      su := Copy(AValue,1,vp - 1);

      if ((dp>mp) and (dp<yp)) or
         ((dp>yp) and (dp<mp)) then
         val(su,da,err)
      else
      if ((mp>dp) and (mp<yp)) or
         ((mp>yp) and (mp<dp)) then
         val(su,mo,err)
      else
      if ((yp>mp) and (yp<dp)) or
         ((yp>dp) and (yp<mp)) then
         val(su,ye,err);

      if err<>0 then Exit;
      Delete(AValue,1,vp);

      AValue := Trim(AValue);

      if VarPos(' ',AValue, vp) > 0 then  // there is space to separate date & time
      begin
        ts := copy(AValue, vp, length(AValue));
        AValue := copy(AValue, 1, vp - 1);
      end;

      if (dp>mp) and
         (dp>yp) then
         val(AValue,da,err)
      else
      if (mp>dp) and
         (mp>yp) then
         val(AValue,mo,err)
      else
      if (yp>mp) and
         (yp>dp) then
         val(AValue,ye,err);

      if err<>0 then Exit;
      if (da>31) then Exit;
      if (mo>12) then Exit;

      if (ts <> '') then  // there is a time part
      begin
        if VarPos(FormatSettings.TimeSeparator,ts,vp)>0 then
        begin
          su := Copy(ts,1,vp - 1); // hour part
          val(su,ho,err);

          if (err <> 0) then Exit;
          if (ho > 23) then Exit;

          Delete(ts,1,vp);

          if VarPos(FormatSettings.TimeSeparator,ts,vp)>0 then // there is a second part
          begin
            su := Copy(ts,1,vp - 1); // minute part
            val(su,mi,err);

            if err <> 0 then Exit;
            Delete(ts,1,vp);

            val(ts,se,err);  // second part
            if (err <> 0) then Exit;
            if (se > 60) then Exit;
          end
          else
          begin
            val(su,mi,err); // minute part
            if (err <> 0) then Exit;
          end;

          if (mi > 59) then Exit;

          Result := true;
        end;
      end
      else
        Result := True;

      try
        ADate := EncodeDate(ye,mo,da) + EncodeTime(ho,mi,se,0);
      except
        Result := False;
      end;
    end;
  end;
end;

function Matches(s0a, s1a: string): Boolean;
const
  larger = '>';
  smaller = '<';
  logand  = '&';
  logor   = '^';
  asterix = '*';
  qmark = '?';
  negation = '!';

var
  matching: boolean;
  done: boolean;
  len: longint;
  lastchar:  char;
  s0,s1,s2,s3: integer;
  oksmaller,oklarger,negflag: boolean;
  compstr: string;
  flag1,flag2,flag3: boolean;
  equal: boolean;
  n1,n2: double;
  code1, code2: Integer;
  dt1,dt2: TDateTime;
  q, i: integer;
begin
  i := 0;

  oksmaller := True;
  oklarger := True;
  flag1 := False;
  flag2 := False;
  flag3 := False;
  negflag := False;
  equal := False;

  { [<>] string [&|] [<>] string }

  // do larger than or larger than or equal

  s2 := Pos(larger,s0a);

  if s2 <> 0 then
  begin
    inc(s2);
    if (s0a[s2 - i] = '=') then
    begin
      Equal := True;
      inc(s2);
    end;

    while (s0a[s2 - i] = ' ') do
      inc(s2);

    s3 := s2;
    len := 0;

    lastchar := #0;

    q := 0;

    while ((s2 - i <= Length(s0a)) and (s0a[s2 - i] <> ' ')) and (s2 <= Length(s0a)) and (odd(q) or ((s0a[s2 - i] <> '&') and (s0a[s2 - i] <> '|')))  do
    begin
      if (s0a[s2 - i] = '"') then
        inc(q);

      if (len = 0) and (s0a[s2 - i] = '"') then
        inc(s3)
      else
        inc(len);

      lastchar := s0a[s2 - i];
      inc(s2);

      if (s2 - i <= Length(s0a)) and (s0a[s2 - i] = ' ') and odd(q) then // skip space if between quotes
      begin
        lastchar := s0a[s2 - i];
        inc(s2);
      end;
    end;

    if (len > 0) and (lastchar = '"') then
      dec(len);

    compstr := Copy(s0a,s3,len);

    //StrLCopy(compstr,s3,len);

    Val(StripThousandSep(s1a),n1,code1);
    Val(StripThousandSep(compstr),n2,code2);

    dt2 := 0;
    if IsDate(compstr,dt2) then
      code2 := 1;

    dt1 := 0;
    if IsDate(s1a,dt1) then
      code1 := 1;

    if (code1 = 0) and (code2 = 0) then {both are numeric types}
    begin
      if equal then
        oklarger := n1 >= n2
      else
        oklarger := n1 > n2;
    end
    else
    begin
      if IsDate(compstr,dt2) and IsDate(s1a,dt1) then
      begin
        if equal then
         oklarger := dt1 >= dt2
        else
         oklarger := dt1 > dt2;
      end
      else
      begin
        if equal then
         oklarger := (CompareStr(compstr,s1a)<=0)
        else
         oklarger := (CompareStr(compstr,s1a)<0);
      end;
    end;
    flag1 := True;
  end;

  equal := False;

  // do smaller than or smaller than or equal
  s2 := Pos(smaller, s0a);
  if (s2 <> 0) then
  begin
    inc(s2);
    if (s0a[s2 - i] = '=') then
      begin
       equal := True;
       inc(s2);
      end;

    lastchar := #0;

    while (s2 - i <= Length(s0a)) and (s0a[s2 - i] =' ') do inc(s2);
    s3 := s2;
    len := 0;
    q := 0;

    while (s2 - i <= Length(s0a)) and (s0a[s2 - i] <> ' ') and (s2 <= Length(s0a)) and (odd(q) or ((s0a[s2 - i] <> '&') and (s0a[s2 - i] <> '|'))) do
    begin
      if s0a[s2 - i] = '"' then
        inc(q);

      if (len = 0) and (s0a[s2 - i] = '"') then
        inc(s3)
      else
        inc(len);

      lastchar := s0a[s2 - i];
      inc(s2);
    end;

    if (len > 0) and (lastchar = '"') then
      dec(len);

    compstr := copy(s0a, s3, len);

    Val(StripThousandSep(s1a),n1,code1);
    Val(StripThousandSep(compstr),n2,code2);
    if IsDate(compstr,dt2) then code2 := 1;
    if IsDate(s1a,dt1) then code1 := 1;

    if (code1 = 0) and (code2 = 0) then // both are numeric types
     begin
      if equal then
       oksmaller := n1 <= n2
      else
       oksmaller := n1 < n2;
     end
    else
     begin
      // check for dates here ?
      if IsDate(compstr, dt2) and IsDate(s1a, dt1) then
       begin
        if equal then
         oksmaller := dt1 <= dt2
        else
         oksmaller := dt1 < dt2;
       end
      else
       begin
        if equal then
          oksmaller := (CompareStr(compstr,s1a)>=0)
        else
          oksmaller := (CompareStr(compstr,s1a)>0);
       end;
     end;

    flag2 := True;
  end;

  s2 := Pos(negation, s0a);

  if (s2 <> 0) then
  begin
    inc(s2);
    while (s0a[s2 - i] = ' ') do
      inc(s2);
    s3 := s2;
    len := 0;

    lastchar := #0;
    q := 0;

    while (s0a[s2 - i] <> ' ') and (s2 <= length(s0a)) and (odd(q) or ((s0a[s2 - i] <> '&') and (s0a[s2 - i] <> '|'))) do
    begin
      if (s0a[s2 - i] = '"') then
        inc(q);

      if (len = 0) and (s0a[s2 - i] = '"') then
        inc(s3)
      else
        inc(len);

      lastchar := s0a[s2 - i];
      inc(s2);
    end;

    if (len > 0) and (lastchar = '"') then
      dec(len);

    compstr := copy(s0a, s3, len);
    flag3 := True;
  end;

  if (flag3) then
  begin
    if Pos(larger, s0a) = 0 then
      flag1 := flag3;
    if Pos(smaller, s0a) = 0 then
      flag2 := flag3;
  end;

  if (Pos(logor, s0a) <> 0) then
    if flag1 or flag2 then
    begin
      matches := oksmaller or oklarger;
      Exit;
    end;

  if (Pos(logand, s0a) <> 0) then
    if flag1 and flag2 then
    begin
      matches := oksmaller and oklarger;
      Exit;
    end;

  if ((Pos(larger, s0a) <> 0) and (oklarger)) or
     ((Pos(smaller, s0a) <> 0) and (oksmaller)) then
  begin
    matches := True;
    Exit;
  end;

  s0 := 1;
  s1 := 1;

  done := (s0a = '') or (s1a = '');

  Matching := True;

  while not done and matching do
  begin
    case s0a[s0 - i] of
    qmark:
      begin
        matching := s1 <= Length(s1a);
        if matching then
        begin
          inc(s0);
          inc(s1);
        end;
      end;
    negation:
      begin
        negflag:=True;
        inc(s0);
      end;
    '"':
      begin
        inc(s0);
      end;
    asterix:
      begin
        repeat
          inc(s0)
        until (s0 - i > Length(s0a)) or (s0a[s0 - i] <> asterix);
        len := Length(s1a) - s1;
        inc(s1,len);
        matching := matches(copy(s0a,s0,Length(s0a)),Copy(s1a, s1, Length(s1a)));

        while (len >= 0) and not matching do
        begin
         dec(s1);
         dec(len);
         matching := matches(copy(s0a,s0,Length(s0a)),Copy(s1a, s1, Length(s1a)));
       end;
       if matching then
       begin
         s0 := Length(s0a) + 1;
         s1 := Length(s1a) + 1;
       end;
    end;
   else
     begin
       if (s0 <= Length(s0a)) and (s1 <= Length(s1a)) then
         matching := s0a[s0 - i] = s1a[s1 - i]
       else
         matching := false;

       if matching then
       begin
         inc(s0);
         inc(s1);
       end;
     end;
   end;

   Done := (s0 > Length(s0a)) and (s1 > Length(s1a));
  end;

  if negflag then
    Matches := not matching
  else
    Matches := matching;
end;

function MatchStr(AValue1, AValue2:string; ACaseSensitive:Boolean = true):Boolean;
begin
  if ACaseSensitive then
    Result := Matches(AValue1,AValue2)
  else
    Result := Matches(UpperCase(AValue1),UpperCase(AValue2));
end;

function StripLogicSpaces(AValue: string): string;
var
  i, k: integer;
  q: integer;
begin
  q := 0;
  i := 1;
  k := length(AValue);
  Result := '';

  while (i <= k) do
  begin
    if AValue[i] = '"' then
      inc(q);

    if (AValue[i] = ' ') then
    begin
      if Odd(q) then
        Result := Result + AValue[i];
    end
    else
      Result := Result + AValue[i];

    inc(i);
  end;
end;

function FirstChar(ACharset: array of char; AValue: string; var spos: Integer): char;
var
  i:Integer;
  q: Integer;
  j: Integer;
begin
  j := 1;
  i := 1;
  q := 0;
  spos := -1;
  Result := #0;

  while i <= Length(AValue) do
  begin
    if AValue[i - j] = '"' then
      inc(q);

    if (CharInSet(AValue[i - j], ACharSet)) and not Odd(q) then
    begin
      spos := i;
      Result := AValue[i - j];
      Break;
    end;
    Inc(i);
  end;
end;

function ClosingParenthesis(s1: string): integer;
var
  i,j,k,r: integer;
begin
  r := 0;
  j := 0;
  k := 0;
  i := 1;

  while (i <= length(s1)) do
  begin
    if (s1[i] = ')') then
      inc(k);

    if (s1[i] = '(') then
      inc(j);

    if (s1[i] = ')') and (j = k) then
    begin
      r := i;
      break;
    end;

    inc(i);
  end;

  Result := r;
end;



function MatchStrEx(AValue1, AValue2: string; ACaseSensitive: Boolean = true): Boolean;
var
  ch,lastop: Char;
  sep,cp: Integer;
  res,newres: Boolean;
  CharArray: array of char;
begin
  AValue1 := Trim(AValue1);
  AValue1 := StripLogicSpaces(AValue1);

  sep := -1;
  if VarPos('=', AValue1, sep) = 1 then
    Delete(AValue1, sep, 1);

  LastOp := #0;
  Res := True;

  SetLength(CharArray,5);
  CharArray[0] := '(';
  CharArray[1] := ';';
  CharArray[2] := '^';
  CharArray[3] := '&';
  CharArray[4] := '|';

  if (AValue1 = '') then
  begin
    Result := (AValue2 = '');
    Exit;
  end;

  if (AValue1 <> '*') and (AValue2 = '') then
  begin
    Result := False;
    Exit;
  end;

  repeat
    ch := FirstChar(CharArray, AValue1, sep);
    if ch <> #0 then
    begin
      if (length(AValue1) > 0) and (AValue1[1] = '(') and (pos('(',AValue1) > 0) then
      begin
        cp := ClosingParenthesis(AValue1);
        NewRes := MatchStrEx(copy(AValue1,2,cp - 2),AValue2,ACaseSensitive);
        delete(AValue1,1,cp);
      end
      else
      begin
        NewRes := MatchStr(Copy(AValue1,1,sep - 1),AValue2,ACaseSensitive);
        Delete(AValue1,1,sep);
      end;

      if LastOp = #0 then
        Res := NewRes
      else
        case LastOp of
        ';','^','|':Res := Res or NewRes;
        '&':Res := Res and NewRes;
        end;

      LastOp := ch;
     end;

  until (ch = #0);

  NewRes := MatchStr(AValue1, AValue2, ACaseSensitive);

  if LastOp = #0 then
    Res := NewRes
  else
    case LastOp of
    ';','^','|':Res := Res or NewRes;
    '&':Res := Res and NewRes;
    end;

  Result := Res;
end;


procedure SelectElementContents(AElement: TJSElement);
begin
  asm
    if (window.getSelection && document.createRange) {
        var sel = window.getSelection();
        var range = document.createRange();
        range.selectNodeContents(AElement);
        sel.removeAllRanges();
        sel.addRange(range);
    } else if (document.selection && document.body.createTextRange) {
        var textRange = document.body.createTextRange();
        textRange.moveToElementText(AElement);
        textRange.select();
    }
  end;
end;

function FormatProp(const AFmt: string; const AArgs: array of const): string;
begin
  Result := Format(AFmt, AArgs, TFormatSettings.Invariant);
end;

function StringIsFloat(N: string): boolean;
var
  f: double;
begin
  Result := TryStrToFloat(N,F);
end;

function StringIsInteger(N: string): boolean;
var
  I: Integer;
begin
  Result := True;
  if Trim(N) = '' then
    Exit(False);

  if (Length(Trim(N)) > 1) and (Trim(N)[1] = '0') then
    Exit(False);

  if not (N[I] in ['0'..'9','-']) then
    Exit(False);

  for I := 2 to Length(N) do
  begin
    if not (N[I] in ['0'..'9']) then
    begin
      Result := False;
      Break;
    end;
  end;
end;

function MD5Hash(N: String): string;
var
  h: string;
begin
  asm
  function md5(inputString) {
    var hc="0123456789abcdef";
    function rh(n) {var j,s="";for(j=0;j<=3;j++) s+=hc.charAt((n>>(j*8+4))&0x0F)+hc.charAt((n>>(j*8))&0x0F);return s;}
    function ad(x,y) {var l=(x&0xFFFF)+(y&0xFFFF);var m=(x>>16)+(y>>16)+(l>>16);return (m<<16)|(l&0xFFFF);}
    function rl(n,c)            {return (n<<c)|(n>>>(32-c));}
    function cm(q,a,b,x,s,t)    {return ad(rl(ad(ad(a,q),ad(x,t)),s),b);}
    function ff(a,b,c,d,x,s,t)  {return cm((b&c)|((~b)&d),a,b,x,s,t);}
    function gg(a,b,c,d,x,s,t)  {return cm((b&d)|(c&(~d)),a,b,x,s,t);}
    function hh(a,b,c,d,x,s,t)  {return cm(b^c^d,a,b,x,s,t);}
    function ii(a,b,c,d,x,s,t)  {return cm(c^(b|(~d)),a,b,x,s,t);}
    function sb(x) {
        var i;var nblk=((x.length+8)>>6)+1;var blks=new Array(nblk*16);for(i=0;i<nblk*16;i++) blks[i]=0;
        for(i=0;i<x.length;i++) blks[i>>2]|=x.charCodeAt(i)<<((i%4)*8);
        blks[i>>2]|=0x80<<((i%4)*8);blks[nblk*16-2]=x.length*8;return blks;
    }
    var i,x=sb(inputString),a=1732584193,b=-271733879,c=-1732584194,d=271733878,olda,oldb,oldc,oldd;
    for(i=0;i<x.length;i+=16) {olda=a;oldb=b;oldc=c;oldd=d;
        a=ff(a,b,c,d,x[i+ 0], 7, -680876936);d=ff(d,a,b,c,x[i+ 1],12, -389564586);c=ff(c,d,a,b,x[i+ 2],17,  606105819);
        b=ff(b,c,d,a,x[i+ 3],22,-1044525330);a=ff(a,b,c,d,x[i+ 4], 7, -176418897);d=ff(d,a,b,c,x[i+ 5],12, 1200080426);
        c=ff(c,d,a,b,x[i+ 6],17,-1473231341);b=ff(b,c,d,a,x[i+ 7],22,  -45705983);a=ff(a,b,c,d,x[i+ 8], 7, 1770035416);
        d=ff(d,a,b,c,x[i+ 9],12,-1958414417);c=ff(c,d,a,b,x[i+10],17,     -42063);b=ff(b,c,d,a,x[i+11],22,-1990404162);
        a=ff(a,b,c,d,x[i+12], 7, 1804603682);d=ff(d,a,b,c,x[i+13],12,  -40341101);c=ff(c,d,a,b,x[i+14],17,-1502002290);
        b=ff(b,c,d,a,x[i+15],22, 1236535329);a=gg(a,b,c,d,x[i+ 1], 5, -165796510);d=gg(d,a,b,c,x[i+ 6], 9,-1069501632);
        c=gg(c,d,a,b,x[i+11],14,  643717713);b=gg(b,c,d,a,x[i+ 0],20, -373897302);a=gg(a,b,c,d,x[i+ 5], 5, -701558691);
        d=gg(d,a,b,c,x[i+10], 9,   38016083);c=gg(c,d,a,b,x[i+15],14, -660478335);b=gg(b,c,d,a,x[i+ 4],20, -405537848);
        a=gg(a,b,c,d,x[i+ 9], 5,  568446438);d=gg(d,a,b,c,x[i+14], 9,-1019803690);c=gg(c,d,a,b,x[i+ 3],14, -187363961);
        b=gg(b,c,d,a,x[i+ 8],20, 1163531501);a=gg(a,b,c,d,x[i+13], 5,-1444681467);d=gg(d,a,b,c,x[i+ 2], 9,  -51403784);
        c=gg(c,d,a,b,x[i+ 7],14, 1735328473);b=gg(b,c,d,a,x[i+12],20,-1926607734);a=hh(a,b,c,d,x[i+ 5], 4,    -378558);
        d=hh(d,a,b,c,x[i+ 8],11,-2022574463);c=hh(c,d,a,b,x[i+11],16, 1839030562);b=hh(b,c,d,a,x[i+14],23,  -35309556);
        a=hh(a,b,c,d,x[i+ 1], 4,-1530992060);d=hh(d,a,b,c,x[i+ 4],11, 1272893353);c=hh(c,d,a,b,x[i+ 7],16, -155497632);
        b=hh(b,c,d,a,x[i+10],23,-1094730640);a=hh(a,b,c,d,x[i+13], 4,  681279174);d=hh(d,a,b,c,x[i+ 0],11, -358537222);
        c=hh(c,d,a,b,x[i+ 3],16, -722521979);b=hh(b,c,d,a,x[i+ 6],23,   76029189);a=hh(a,b,c,d,x[i+ 9], 4, -640364487);
        d=hh(d,a,b,c,x[i+12],11, -421815835);c=hh(c,d,a,b,x[i+15],16,  530742520);b=hh(b,c,d,a,x[i+ 2],23, -995338651);
        a=ii(a,b,c,d,x[i+ 0], 6, -198630844);d=ii(d,a,b,c,x[i+ 7],10, 1126891415);c=ii(c,d,a,b,x[i+14],15,-1416354905);
        b=ii(b,c,d,a,x[i+ 5],21,  -57434055);a=ii(a,b,c,d,x[i+12], 6, 1700485571);d=ii(d,a,b,c,x[i+ 3],10,-1894986606);
        c=ii(c,d,a,b,x[i+10],15,   -1051523);b=ii(b,c,d,a,x[i+ 1],21,-2054922799);a=ii(a,b,c,d,x[i+ 8], 6, 1873313359);
        d=ii(d,a,b,c,x[i+15],10,  -30611744);c=ii(c,d,a,b,x[i+ 6],15,-1560198380);b=ii(b,c,d,a,x[i+13],21, 1309151649);
        a=ii(a,b,c,d,x[i+ 4], 6, -145523070);d=ii(d,a,b,c,x[i+11],10,-1120210379);c=ii(c,d,a,b,x[i+ 2],15,  718787259);
        b=ii(b,c,d,a,x[i+ 9],21, -343485551);a=ad(a,olda);b=ad(b,oldb);c=ad(c,oldc);d=ad(d,oldd);
    }
    return rh(a)+rh(b)+rh(c)+rh(d);
  }
  h = md5(N);
end;
  Result := h;
end;


end.
